/**
 * Sorting a class's assignments into the three piles a teacher actually thinks
 * in.
 *
 * A single list ordered by due date mixes "I owe these scores" in with "this
 * isn't until next month" and months of finished work, so the one thing a
 * teacher opens this page to do — find what still needs grading — is the
 * hardest thing to see.
 */

export type AssignmentBucket = 'needsGrading' | 'current' | 'graded';

export interface BucketableAssignment {
  id: string;
  dueOn?: unknown;
  position?: number | null;
}

export interface BucketCounts {
  /**
   * Students the teacher has made a decision about, out of those the
   * assignment applies to.
   *
   * A decision is a number, or Missing, or Excused — all three are the teacher
   * having dealt with that student. Only a blank is outstanding work. Marking
   * someone Missing must not leave the assignment sitting in the grading
   * backlog forever, since there is nothing further to enter for them.
   */
  resolvedCount: number;
  eligibleCount: number;
}

export interface AssignmentGroup<T> {
  key: AssignmentBucket;
  title: string;
  assignments: T[];
  /**
   * Finished work is the bulk of a term and the least likely to be wanted, so
   * it arrives folded away; the two piles with work left in them do not.
   */
  defaultOpen: boolean;
}

export function localDateString(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dueOnString(assignment: BucketableAssignment): string {
  return assignment.dueOn ? String(assignment.dueOn) : '';
}

/**
 * Nothing outstanding wins over everything else: an assignment every student
 * has been dealt with is finished whether or not its due date has passed. After
 * that it is simply whether the date has gone by.
 */
export function bucketFor(
  assignment: BucketableAssignment,
  counts: BucketCounts,
  today: string
): AssignmentBucket {
  if (counts.resolvedCount >= counts.eligibleCount) return 'graded';
  const dueOn = dueOnString(assignment);
  // No due date means nothing has come due, so it sits with current work
  // rather than being reported as overdue grading.
  if (dueOn && dueOn <= today) return 'needsGrading';
  return 'current';
}

function byDueDate(direction: 1 | -1) {
  return <T extends BucketableAssignment>(first: T, second: T) => {
    const firstDue = dueOnString(first);
    const secondDue = dueOnString(second);
    if (firstDue && secondDue && firstDue !== secondDue) {
      return firstDue < secondDue ? -direction : direction;
    }
    // Undated work sorts last either way, then falls back to the order an
    // admin arranged it in.
    if (firstDue && !secondDue) return -1;
    if (!firstDue && secondDue) return 1;
    return (first.position ?? 0) - (second.position ?? 0);
  };
}

/** Oldest first for a backlog; soonest first for what is coming; newest first for history. */
const SORTS: Record<AssignmentBucket, ReturnType<typeof byDueDate>> = {
  needsGrading: byDueDate(1),
  current: byDueDate(1),
  graded: byDueDate(-1),
};

const META: Record<AssignmentBucket, { title: string; defaultOpen: boolean }> = {
  needsGrading: { title: 'Needs grading', defaultOpen: true },
  current: { title: 'Current', defaultOpen: true },
  graded: { title: 'Graded', defaultOpen: false },
};

const ORDER: AssignmentBucket[] = ['needsGrading', 'current', 'graded'];

/**
 * The three groups in the order a teacher works through them, with empty ones
 * dropped so an inbox-zero class does not show a wall of empty headings.
 */
export function groupAssignments<T extends BucketableAssignment>(
  assignments: ReadonlyArray<T>,
  countsFor: (assignment: T) => BucketCounts,
  today = localDateString()
): AssignmentGroup<T>[] {
  const buckets: Record<AssignmentBucket, T[]> = { needsGrading: [], current: [], graded: [] };

  for (const assignment of assignments) {
    buckets[bucketFor(assignment, countsFor(assignment), today)].push(assignment);
  }

  return ORDER
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({
      key,
      ...META[key],
      assignments: buckets[key].sort(SORTS[key]),
    }));
}
